import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Plant, Wall, Snowflake, Drop, HardHat, Leaf, Sparkle, Flower, Buildings, ClipboardText } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { SERVICES_RESIDENTIAL, SERVICES_COMMERCIAL, PHOTOS } from "../lib/data";

const ICONS = { Plant, Wall, Snowflake, Drop, HardHat, Leaf, Sparkle, Flower, Buildings, ClipboardText };

function ServiceRow({ s, idx }) {
    const Icon = ICONS[s.icon] || Plant;
    const reversed = idx % 2 === 1;
    return (
        <div data-testid={`service-${s.title}`} className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-center py-16 border-b border-brand-border last:border-b-0`}>
            <div className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
                {s.image ? (
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                        <img src={s.image} alt={s.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                ) : (
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden relative bg-brand-primary text-white flex items-center justify-center">
                        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,#6B8B5F_0%,transparent_55%),radial-gradient(circle_at_80%_80%,#C9A86A_0%,transparent_55%)]" />
                        <div className="relative text-center px-8">
                            <div className="inline-flex p-5 rounded-full bg-white/10 backdrop-blur-sm">
                                <Icon size={56} weight="duotone" className="text-white" />
                            </div>
                            <p className="mt-6 text-xs uppercase tracking-[0.3em] text-[#D6E0CF] font-bold">{s.icon}</p>
                            <p className="mt-2 font-serif text-2xl">{s.title}</p>
                        </div>
                    </div>
                )}
            </div>
            <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-lg bg-brand-surfaceAlt text-brand-primary">
                        <Icon size={22} weight="duotone" />
                    </div>
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-secondary font-bold">{s.icon}</span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-brand-primary font-medium leading-tight">{s.title}</h3>
                <p className="mt-4 text-base text-brand-textMuted leading-relaxed">{s.desc}</p>
                <ul className="mt-6 space-y-3">
                    {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3">
                            <Check size={18} weight="bold" className="text-brand-accent mt-1 shrink-0" />
                            <span className="text-sm text-brand-text">{b}</span>
                        </li>
                    ))}
                </ul>
                <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand-primary hover:gap-3 transition-all">
                    Request a quote <ArrowRight size={16} weight="bold" />
                </Link>
            </div>
        </div>
    );
}

export default function ServicesPage() {
    return (
        <div data-testid="services-page">
            <PageHero
                eyebrow="Services"
                title="Design, build, maintain — every detail of the property."
                sub="A complete service catalogue for homeowners, HOAs, and commercial clients across the DMV."
                image={PHOTOS.bluestonePatioDusk}
            />

            {/* Residential */}
            <section className="px-4 sm:px-8 py-12">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-8 pb-8 border-b border-brand-border">
                        <div>
                            <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-3">For homeowners</p>
                            <h2 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-[1.1] font-medium tracking-tight">Residential Services</h2>
                        </div>
                        <p className="text-brand-textMuted max-w-md text-sm">From a single garden bed to a complete property redesign — we treat every home like our own.</p>
                    </div>
                    {SERVICES_RESIDENTIAL.map((s, i) => <ServiceRow key={s.title} s={s} idx={i} />)}
                </div>
            </section>

            {/* Commercial */}
            <section className="px-4 sm:px-8 py-12 bg-brand-surfaceAlt">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-8 pb-8 border-b border-brand-border">
                        <div>
                            <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary font-bold mb-3">For businesses</p>
                            <h2 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-[1.1] font-medium tracking-tight">Commercial Services</h2>
                        </div>
                        <p className="text-brand-textMuted max-w-md text-sm">Predictable, contracted service for HOAs, office parks, and commercial campuses — with 24/7 storm response.</p>
                    </div>
                    {SERVICES_COMMERCIAL.map((s, i) => <ServiceRow key={s.title} s={s} idx={i} />)}
                </div>
            </section>

            <CTABanner
                eyebrow="Get started"
                title="Tell us about your property."
                sub="We'll prepare a no-obligation estimate after a free on-site consultation."
            />
        </div>
    );
}
